import { useSession } from '@grimme/next-grimme-auth';
import { AxiosRequestConfig, RawAxiosRequestConfig } from 'axios';
import { useCallback } from 'react';
import useSWR, { Key, SWRConfiguration, SWRResponse } from 'swr';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation';
import { SWRResult } from './types';
import { isJwtCurrentlyValid, useAuthSWRKey } from './use-auth-swr-key';

/**
 * Wrapper around useSWR to fetch data from an authenticated API endpoint.
 *
 * The Axios config can be passed in as a parameter. But the Authorization header
 * will be set automatically with the access token from the session. Setting a
 * header config will override the Authorization header.
 *
 * @param url - The URL to fetch
 * @param config - The Axios config to use
 * @param swrConfig - An optional SWR config to use
 * @returns {SWRResult} The SWR result
 */
// TODO: We should deprecated this function and use useSWRAuthenticatedForServices instead
export function useDeprecatedSWRAuthenticated<T>(
  {
    config,
    url,
  }: {
    config?: AxiosRequestConfig;
    url: string | undefined | null;
  },
  swrConfig?: SWRConfiguration,
): SWRResult<T> {
  // Get the session data and status
  const { data: sessionData, status } = useSession();
  // Use SWR to fetch the data
  const { data, error, isLoading, isValidating, mutate } = useSWR<T>(
    url
      ? () => {
          // If the user is not authenticated, return null to not fetch anything
          if (status !== 'authenticated') return null;

          // Return the url and config for the SWR fetcher and
          // set the Authorization header with the access token
          return {
            config: {
              headers: {
                Authorization: `Bearer ${sessionData?.accessToken}`,
              },
              ...config,
            },
            url,
          };
        }
      : null,
    swrConfig,
  );

  // Return the SWR result with the data, error and loading status
  return {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  };
}

export const useAuthSWR = <TResponseData = unknown, TKey = Key, Error = any>(
  key: TKey | null | undefined,
  apiCall: (
    config: RawAxiosRequestConfig | undefined,
    key: TKey,
  ) => Promise<TResponseData>,
  options?: SWRConfiguration<TResponseData, Error>,
): SWRResponse<TResponseData, Error> => {
  const composeAuthKey = useAuthSWRKey();

  const fetcher = useCallback(
    ([key, accessToken]: [TKey, string]) => {
      // NOTE: Include the token in the headers for the API call
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken || ''}`,
        },
      };

      return apiCall(config, key);
    },
    [apiCall],
  );

  // NOTE: Return useSWR as it is so that the caller can use it as if it was a default useSWR without losing the type
  return useSWR<TResponseData, Error>(composeAuthKey(key), fetcher, {
    errorRetryCount: 5,
    errorRetryInterval: 100,
    shouldRetryOnError: true,
    ...options,
  });
};

export const useAuthSWRInfinite = <
  TResponseData = unknown,
  TKey = Key,
  Error = any,
>(
  getKey: (
    pageIndex: number,
    previousPageData: TResponseData | null,
  ) => TKey | null,
  apiCall: (
    config: RawAxiosRequestConfig | undefined,
    key: TKey,
  ) => Promise<TResponseData>,
  options?: SWRInfiniteConfiguration<TResponseData, Error>,
) => {
  // NOTE: Get the session data and status
  const { data: sessionData } = useSession();
  const accessToken = sessionData?.accessToken;
  const shouldFetch = !!accessToken && isJwtCurrentlyValid(accessToken);

  //NOTE: Returns cache key per page or null to stop fetching
  const getAuthKey = useCallback(
    (pageIndex: number, previousPageData: TResponseData | null) => {
      if (!shouldFetch) return null;

      const key = getKey(pageIndex, previousPageData);

      return key ? ([key, accessToken as string] as const) : null;
    },
    [shouldFetch, getKey, accessToken],
  );

  const fetcher = useCallback(
    ([key, accessToken]: [TKey, string]) => {
      // NOTE: Include the token in the headers for the API call
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken || ''}`,
        },
      };

      return apiCall(config, key);
    },
    [apiCall],
  );

  return useSWRInfinite<TResponseData, Error>(getAuthKey, fetcher, options);
};

export type AuthSWRMutationArgs<TArgs> = {
  args: TArgs;
  config: AxiosRequestConfig;
};

export const useAuthSWRMutation = <TArgs, TResponseData = unknown>(
  key: Key,
  apiCall: (
    config: RawAxiosRequestConfig | undefined,
    args: TArgs,
  ) => Promise<TResponseData>,
  // NOTE: Maybe there is a better way to type the unknowns here
  options?: SWRMutationConfiguration<
    TResponseData,
    unknown,
    Key,
    AuthSWRMutationArgs<TArgs>
  >,
) => {
  // NOTE: Get the session data and status
  const { data: sessionData } = useSession();
  const accessToken = sessionData?.accessToken;

  const fetcher = async (
    _: Key,
    { arg: args }: { arg: AuthSWRMutationArgs<TArgs> },
  ) => {
    // NOTE: Include the token in the headers for the API call

    return apiCall(args.config, args.args);
  };
  // NOTE: Return useSWRMutation as it is so that the caller can use it as if it was a default useSWR without losing the type
  const { trigger, ...rest } = useSWRMutation(key, fetcher, options);

  // Stable trigger: an inline arrow here means a new reference every render,
  // which breaks callers that put `trigger` in an effect dep array (endless
  // re-fire loop). Memoize on the token so a refresh still uses the new one.
  const triggerWithConfig = useCallback(
    (args: TArgs) =>
      trigger({
        args,
        config: {
          headers: {
            Authorization: `Bearer ${accessToken || ''}`,
            ContentType: 'application/json',
          },
        },
      }),
    [trigger, accessToken],
  );

  return {
    ...rest,
    trigger: triggerWithConfig,
  };
};
